import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Monitor, Tablet, Wifi, WifiOff, ArrowRight } from 'lucide-react';

const APPS = [
  {
    name: 'AbbeyZone',
    path: '/home',
    icon: Monitor,
    color: 'bg-slate-800',
    borderColor: 'border-slate-200',
    description: 'Full management platform for scheduling, stocktaking, reporting, incentives, and administration.',
    when: 'Use when you need the complete audit workflow — wizard, deliveries, credits, sales, calculations, PDF reports, and client portal management.',
    connectivity: [
      { label: 'Online', detail: 'Full functionality — all modules, live data, real-time sync', online: true },
      { label: 'Offline', detail: 'Limited — downloaded sessions can be edited and synced later', online: false },
    ],
  },
  {
    name: 'AbbeyZone Count',
    path: '/count/sessions',
    icon: Tablet,
    color: 'bg-cyan-700',
    borderColor: 'border-cyan-200',
    description: 'Dedicated counting app optimised for tablet field auditors. Fast product entry, photo observations, and sync.',
    when: 'Use on-site when counting stock. Download the session before visiting the site, count offline, then sync results when back online.',
    connectivity: [
      { label: 'Online', detail: 'Download sessions, sync counts, upload photos, view reconciliation', online: true },
      { label: 'Offline', detail: 'Full counting with local save. All data persisted in device storage until sync', online: false },
    ],
  },
];

export default function AppsPage() {
  const { user } = useAuth();

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="apps-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/home">
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Apps</h1>
          <p className="text-sm text-slate-500">Choose the right tool for your task</p>
        </div>
      </div>

      {/* App tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {APPS.map(app => {
          const Icon = app.icon;
          return (
            <Card
              key={app.name}
              className={`${app.borderColor} border-2 hover:shadow-lg transition-all`}
              data-testid={`app-tile-${app.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className={`${app.color} w-12 h-12 rounded-xl flex items-center justify-center`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{app.name}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">{app.description}</p>

                {/* When to use */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-1">When to use</p>
                  <p className="text-xs text-slate-600">{app.when}</p>
                </div>

                {/* Connectivity */}
                <div className="space-y-2">
                  {app.connectivity.map(c => (
                    <div key={c.label} className="flex items-start gap-2">
                      {c.online ? (
                        <Wifi className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                      ) : (
                        <WifiOff className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      )}
                      <div>
                        <p className="text-xs font-medium text-slate-700">{c.label}</p>
                        <p className="text-xs text-slate-500">{c.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Launch button */}
                <Link to={app.path}>
                  <Button className="w-full mt-2" variant="outline" data-testid={`app-launch-${app.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    Open {app.name}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
